/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Reelplicate API
 * OpenAPI spec version: 1.0
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

import type {
  ApiErrorDto,
  CommentDto,
  CommentThreadDto,
  CreateCommentDto,
  UpdateCommentDto,
} from '../../model';

import { customInstance } from '../../../mutator';
import type { ErrorType, BodyType } from '../../../mutator';

const withQueryKey = <T extends object, K>(
  query: T,
  queryKey: K,
): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary A draft's comments: top-level oldest first, each with its replies.
 */
export const listComments = (id: string, approvalId: string, signal?: AbortSignal) => {
  return customInstance<CommentThreadDto>({
    url: `/api/v1/workspaces/${id}/approvals/${approvalId}/comments`,
    method: 'GET',
    signal,
  });
};

export const getListCommentsQueryKey = (id: string, approvalId: string) => {
  return [`/api/v1/workspaces/${id}/approvals/${approvalId}/comments`] as const;
};

export const getListCommentsQueryOptions = <
  TData = Awaited<ReturnType<typeof listComments>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  approvalId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listComments>>, TError, TData>
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListCommentsQueryKey(id, approvalId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listComments>>> = ({ signal }) =>
    listComments(id, approvalId, signal);

  return {
    queryKey,
    queryFn,
    enabled:
      id !== null && id !== undefined && approvalId !== null && approvalId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof listComments>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type ListCommentsQueryResult = NonNullable<
  Awaited<ReturnType<typeof listComments>>
>;
export type ListCommentsQueryError = ErrorType<ApiErrorDto>;

export function useListComments<
  TData = Awaited<ReturnType<typeof listComments>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  approvalId: string,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listComments>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listComments>>,
          TError,
          Awaited<ReturnType<typeof listComments>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListComments<
  TData = Awaited<ReturnType<typeof listComments>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  approvalId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listComments>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listComments>>,
          TError,
          Awaited<ReturnType<typeof listComments>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListComments<
  TData = Awaited<ReturnType<typeof listComments>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  approvalId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listComments>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary A draft's comments: top-level oldest first, each with its replies.
 */

export function useListComments<
  TData = Awaited<ReturnType<typeof listComments>>,
  TError = ErrorType<ApiErrorDto>,
>(
  id: string,
  approvalId: string,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof listComments>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListCommentsQueryOptions(id, approvalId, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Comment on a draft, or reply to a comment. Mentioned people are notified.
 */
export const createComment = (
  id: string,
  approvalId: string,
  createCommentDto: BodyType<CreateCommentDto>,
  signal?: AbortSignal,
) => {
  return customInstance<CommentDto>({
    url: `/api/v1/workspaces/${id}/approvals/${approvalId}/comments`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: createCommentDto,
    signal,
  });
};

export const getCreateCommentMutationKey = () => ['createComment'] as const;

export const getCreateCommentMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createComment>>,
    TError,
    CreateCommentMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createComment>>,
  TError,
  CreateCommentMutationVariables,
  TContext
> => {
  const mutationKey = getCreateCommentMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createComment>>,
    CreateCommentMutationVariables
  > = (props) => {
    const { id, approvalId, data } = props ?? {};

    return createComment(id, approvalId, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateCommentMutationResult = NonNullable<
  Awaited<ReturnType<typeof createComment>>
>;
export type CreateCommentMutationBody = BodyType<CreateCommentDto>;
export type CreateCommentMutationError = ErrorType<ApiErrorDto>;
export type CreateCommentMutationVariables = {
  id: string;
  approvalId: string;
  data: BodyType<CreateCommentDto>;
};

/**
 * @summary Comment on a draft, or reply to a comment. Mentioned people are notified.
 */
export const useCreateComment = <TError = ErrorType<ApiErrorDto>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createComment>>,
      TError,
      CreateCommentMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof createComment>>,
  TError,
  CreateCommentMutationVariables,
  TContext
> => {
  return useMutation(getCreateCommentMutationOptions(options), queryClient);
};
/**
 * @summary You've read this draft's comments: its mentions and replies stop showing as unread.
 */
export const markCommentsSeen = (
  id: string,
  approvalId: string,
  signal?: AbortSignal,
) => {
  return customInstance<void>({
    url: `/api/v1/workspaces/${id}/approvals/${approvalId}/comments/seen`,
    method: 'POST',
    signal,
  });
};

export const getMarkCommentsSeenMutationKey = () => ['markCommentsSeen'] as const;

export const getMarkCommentsSeenMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof markCommentsSeen>>,
    TError,
    MarkCommentsSeenMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof markCommentsSeen>>,
  TError,
  MarkCommentsSeenMutationVariables,
  TContext
> => {
  const mutationKey = getMarkCommentsSeenMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof markCommentsSeen>>,
    MarkCommentsSeenMutationVariables
  > = (props) => {
    const { id, approvalId } = props ?? {};

    return markCommentsSeen(id, approvalId);
  };

  return { mutationFn, ...mutationOptions };
};

export type MarkCommentsSeenMutationResult = NonNullable<
  Awaited<ReturnType<typeof markCommentsSeen>>
>;

export type MarkCommentsSeenMutationError = ErrorType<ApiErrorDto>;
export type MarkCommentsSeenMutationVariables = { id: string; approvalId: string };

/**
 * @summary You've read this draft's comments: its mentions and replies stop showing as unread.
 */
export const useMarkCommentsSeen = <TError = ErrorType<ApiErrorDto>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof markCommentsSeen>>,
      TError,
      MarkCommentsSeenMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof markCommentsSeen>>,
  TError,
  MarkCommentsSeenMutationVariables,
  TContext
> => {
  return useMutation(getMarkCommentsSeenMutationOptions(options), queryClient);
};
/**
 * @summary Edit your comment. People newly mentioned are notified.
 */
export const updateComment = (
  id: string,
  commentId: string,
  updateCommentDto: BodyType<UpdateCommentDto>,
  signal?: AbortSignal,
) => {
  return customInstance<CommentDto>({
    url: `/api/v1/workspaces/${id}/comments/${commentId}`,
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    data: updateCommentDto,
    signal,
  });
};

export const getUpdateCommentMutationKey = () => ['updateComment'] as const;

export const getUpdateCommentMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateComment>>,
    TError,
    UpdateCommentMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateComment>>,
  TError,
  UpdateCommentMutationVariables,
  TContext
> => {
  const mutationKey = getUpdateCommentMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateComment>>,
    UpdateCommentMutationVariables
  > = (props) => {
    const { id, commentId, data } = props ?? {};

    return updateComment(id, commentId, data);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateCommentMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateComment>>
>;
export type UpdateCommentMutationBody = BodyType<UpdateCommentDto>;
export type UpdateCommentMutationError = ErrorType<ApiErrorDto>;
export type UpdateCommentMutationVariables = {
  id: string;
  commentId: string;
  data: BodyType<UpdateCommentDto>;
};

/**
 * @summary Edit your comment. People newly mentioned are notified.
 */
export const useUpdateComment = <TError = ErrorType<ApiErrorDto>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof updateComment>>,
      TError,
      UpdateCommentMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof updateComment>>,
  TError,
  UpdateCommentMutationVariables,
  TContext
> => {
  return useMutation(getUpdateCommentMutationOptions(options), queryClient);
};
/**
 * @summary Delete a comment: yours, or anyone's if you're an owner.
 */
export const deleteComment = (id: string, commentId: string, signal?: AbortSignal) => {
  return customInstance<void>({
    url: `/api/v1/workspaces/${id}/comments/${commentId}`,
    method: 'DELETE',
    signal,
  });
};

export const getDeleteCommentMutationKey = () => ['deleteComment'] as const;

export const getDeleteCommentMutationOptions = <
  TError = ErrorType<ApiErrorDto>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteComment>>,
    TError,
    DeleteCommentMutationVariables,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteComment>>,
  TError,
  DeleteCommentMutationVariables,
  TContext
> => {
  const mutationKey = getDeleteCommentMutationKey();
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteComment>>,
    DeleteCommentMutationVariables
  > = (props) => {
    const { id, commentId } = props ?? {};

    return deleteComment(id, commentId);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteCommentMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteComment>>
>;

export type DeleteCommentMutationError = ErrorType<ApiErrorDto>;
export type DeleteCommentMutationVariables = { id: string; commentId: string };

/**
 * @summary Delete a comment: yours, or anyone's if you're an owner.
 */
export const useDeleteComment = <TError = ErrorType<ApiErrorDto>, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof deleteComment>>,
      TError,
      DeleteCommentMutationVariables,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof deleteComment>>,
  TError,
  DeleteCommentMutationVariables,
  TContext
> => {
  return useMutation(getDeleteCommentMutationOptions(options), queryClient);
};
